// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Header,
  Table,
  Button,
  Icon,
  Label,
  Message,
  Segment,
  Input,
  Dropdown,
  Pagination,
  Modal,
} from 'semantic-ui-react';
import { advisorApi, Advisor } from '../api/advisorApi';
import { formatDate } from '@loa/theme';
function AdvisorListPage() {
  const navigate = useNavigate();
  const [advisors, setAdvisors] = useState<Advisor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [advisorToDelete, setAdvisorToDelete] = useState<Advisor | null>(null);
  const [deleting, setDeleting] = useState(false);

  const statusOptions = [
    { key: 'all', value: '', text: 'All Statuses' },
    { key: 'invited', value: 'invited', text: 'Invited' },
    { key: 'pending_activation', value: 'pending_activation', text: 'Pending Activation' },
    { key: 'active', value: 'active', text: 'Active' },
    { key: 'inactive', value: 'inactive', text: 'Inactive' },
    { key: 'suspended', value: 'suspended', text: 'Suspended' },
    { key: 'terminated', value: 'terminated', text: 'Terminated' },
  ];

  useEffect(() => {
    loadAdvisors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, statusFilter]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(0);
  }, [searchQuery, statusFilter, pageSize]);

  const loadAdvisors = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await advisorApi.getAll(currentPage, pageSize, 'createdAt', 'DESC');
      setAdvisors(response.content);
      setTotalPages(response.totalPages);
      setTotalElements(response.totalElements);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load advisors');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      loadAdvisors();
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await advisorApi.search(searchQuery, undefined, currentPage, pageSize);
      setAdvisors(response.content);
      setTotalPages(response.totalPages);
      setTotalElements(response.totalElements);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to search advisors');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (advisor: Advisor) => {
    setAdvisorToDelete(advisor);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!advisorToDelete?.id) return;

    setDeleting(true);
    try {
      await advisorApi.delete(advisorToDelete.id);
      setDeleteModalOpen(false);
      setAdvisorToDelete(null);
      // Reload advisors list
      await loadAdvisors();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete advisor');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setAdvisorToDelete(null);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'invited':
        return 'blue';
      case 'pending_activation':
        return 'yellow';
      case 'active':
        return 'green';
      case 'inactive':
        return 'grey';
      case 'suspended':
        return 'orange';
      case 'terminated':
        return 'red';
      default:
        return 'grey';
    }
  };

  const formatStatusLabel = (status: string) =>
    status.replace(/_/g, ' ').toUpperCase();

  const handleActivateClick = async (advisor: Advisor) => {
    if (!advisor.id) return;
    setError(null);
    try {
      const updated = await advisorApi.updateStatus(advisor.id, 'active');
      setAdvisors((prev) =>
        prev.map((a) => (a.id === updated.id ? updated : a)),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to activate advisor');
    }
  };

  return (
    <Container style={{ marginTop: '2rem' }}>
      <Header as='h1'>
        <Icon name='users' />
        <Header.Content>
          Advisors
          <Header.Subheader>Manage advisors across all brokerages</Header.Subheader>
        </Header.Content>
      </Header>

      {error && (
        <Message negative onDismiss={() => setError(null)}>
          <Message.Header>Error</Message.Header>
          <p>{error}</p>
        </Message>
      )}

      <Segment>
        <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
          <Input
            icon='search'
            placeholder='Search by name or email...'
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e: React.KeyboardEvent) => {
              if (e.key === 'Enter') handleSearch();
            }}
            style={{ flex: 1 }}
          />
          <Button primary onClick={handleSearch}>
            Search
          </Button>
          <Dropdown
            selection
            options={statusOptions}
            value={statusFilter}
            onChange={(_, data) => setStatusFilter(data.value as string)}
            placeholder='Filter by status'
          />
          <Button positive onClick={() => navigate('/advisors/new')}>
            <Icon name='plus' />
            Invite Advisor
          </Button>
        </div>

        {loading && (
          <p>Loading advisors...</p>
        )}
        {!loading && advisors.length === 0 && (
          <Message info>
            <Message.Header>No advisors found</Message.Header>
            <p>Start by inviting your first advisor using the button above.</p>
          </Message>
        )}
        {!loading && advisors.length > 0 && (
          <>
            <Table celled>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Name</Table.HeaderCell>
                  <Table.HeaderCell>Email</Table.HeaderCell>
                  <Table.HeaderCell>Brokerage</Table.HeaderCell>
                  <Table.HeaderCell>License #</Table.HeaderCell>
                  <Table.HeaderCell>Role</Table.HeaderCell>
                  <Table.HeaderCell>Status</Table.HeaderCell>
                  <Table.HeaderCell>Invited</Table.HeaderCell>
                  <Table.HeaderCell>Actions</Table.HeaderCell>
                </Table.Row>
              </Table.Header>

              <Table.Body>
                {advisors.map((advisor) => (
                  <Table.Row key={advisor.id}>
                    <Table.Cell>
                      {advisor.firstName} {advisor.lastName}
                    </Table.Cell>
                    <Table.Cell>{advisor.email}</Table.Cell>
                    <Table.Cell>{advisor.brokerageName || 'Unknown'}</Table.Cell>
                    <Table.Cell>{advisor.licenseNumber || 'N/A'}</Table.Cell>
                    <Table.Cell>
                      <Label color={advisor.role === 'ADMIN' ? 'purple' : 'blue'} size='small'>
                        {advisor.role}
                      </Label>
                    </Table.Cell>
                    <Table.Cell>
                      <Label color={getStatusColor(advisor.status)} size='small'>
                        {formatStatusLabel(advisor.status)}
                      </Label>
                    </Table.Cell>
                    <Table.Cell>{formatDate(advisor.invitedAt)}</Table.Cell>
                    <Table.Cell>
                      <Button.Group size='small'>
                        {advisor.status === 'pending_activation' && (
                          <Button
                            icon='check'
                            color='green'
                            title='Activate advisor'
                            onClick={() => handleActivateClick(advisor)}
                          />
                        )}
                        <Button
                          icon='edit'
                          color='blue'
                          onClick={() => navigate(`/advisors/${advisor.id}/edit`)}
                        />
                        <Button
                          icon='trash'
                          color='red'
                          onClick={() => handleDeleteClick(advisor)}
                        />
                      </Button.Group>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>

            <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ color: '#666' }}>
                Showing {currentPage * pageSize + 1}-{Math.min((currentPage + 1) * pageSize, totalElements)} of {totalElements} advisor{totalElements === 1 ? '' : 's'}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span>Items per page:</span>
                <Dropdown
                  compact
                  selection
                  value={pageSize}
                  options={[
                    { key: 10, text: '10', value: 10 },
                    { key: 20, text: '20', value: 20 },
                    { key: 50, text: '50', value: 50 },
                    { key: 100, text: '100', value: 100 }
                  ]}
                  onChange={(_e, data) => {
                    setPageSize(data.value as number);
                    setCurrentPage(0); // Reset to first page when changing page size
                  }}
                />
                <Pagination
                  activePage={currentPage + 1}
                  totalPages={totalPages}
                  onPageChange={(_e, data) => setCurrentPage((data.activePage as number) - 1)}
                  siblingRange={1}
                  boundaryRange={0}
                  ellipsisItem={null}
                  firstItem={null}
                  lastItem={null}
                  prevItem={{ content: <Icon name='chevron left' />, icon: true }}
                  nextItem={{ content: <Icon name='chevron right' />, icon: true }}
                />
              </div>
            </div>
          </>
        )}
      </Segment>

      {/* Delete Confirmation Modal */}
      <Modal
        open={deleteModalOpen}
        onClose={handleDeleteCancel}
        size='small'
      >
        <Header icon='trash' content='Delete Advisor' />
        <Modal.Content>
          <p>
            Are you sure you want to delete advisor{' '}
            <strong>
              {advisorToDelete?.firstName} {advisorToDelete?.lastName}
            </strong>{' '}
            ({advisorToDelete?.email})?
          </p>
          <p>This action cannot be undone.</p>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={handleDeleteCancel} disabled={deleting}>
            <Icon name='remove' /> Cancel
          </Button>
          <Button negative onClick={handleDeleteConfirm} loading={deleting}>
            <Icon name='checkmark' /> Delete
          </Button>
        </Modal.Actions>
      </Modal>
    </Container>
  );
}

export default AdvisorListPage;
